import { ReactElement, useState, useMemo } from 'react'
import { assemblyKindToLabel } from '@domainConstants'
import { useIsOrganisationAdmin } from '@hooks'
import { isFunction } from 'lodash-es'
import CopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import EditIcon from '@mui/icons-material/Edit'
import Filter2Icon from '@mui/icons-material/Filter2'
import StorageIcon from '@mui/icons-material/Storage'
import { TableCell, TableRow, Checkbox, IconButton, Tooltip, Typography, Box } from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles/'
import { AlertDialog, IconButtonProgress } from '@ui/feedback'
import AssemblyPlot from '../AssemblyPlot'
import { calculateCumulativeWeight, generatePlottingLayers } from '../utils'

interface Props {
  assembly: Assembly
  onDelete: () => void
  onDuplicate: () => void
  onEdit: () => void
  onSetStandard: () => void
  isSettingStandard: boolean
  onStore?: () => void
  onCopy?: () => void
  onDownload: () => Promise<void>
  'data-cy'?: string
  earthMassForce: number
}

const AssemblyListItem = ({
  assembly,
  onDelete,
  onDownload,
  onEdit,
  onDuplicate,
  onSetStandard,
  isSettingStandard,
  onStore,
  onCopy,
  'data-cy': dataCy,
  earthMassForce,
}: Props): ReactElement => {
  const { name, guid, short_name } = assembly
  const isProjectAssembly = isFunction(onStore)
  const [isDownloading, setIsDownloading] = useState(false)
  // Applies only to the organisation's library (project assemblies belong to
  // the project, and every member may edit those): reading stays, everything
  // that would write is not offered to a member who is not an admin.
  const { isOrganisationAdmin } = useIsOrganisationAdmin()
  const readOnly = !isProjectAssembly && !isOrganisationAdmin

  const defaultSX: SxProps<Theme> = ({ palette, spacing }: Theme) => ({
    '&:last-child td, &:last-child th': { border: 0 },
    '& td': {
      padding: spacing(1),
    },
    '&:hover': {
      background: palette.grey[100],
    },
    cursor: 'pointer',
    color: {
      background: palette.background.default,
    },
  })

  const cumulativeWeight = useMemo(() => {
    return calculateCumulativeWeight(
      generatePlottingLayers(
        assembly.layers,
        assembly.usage_type === 'Outer' || assembly.usage_type === 'Inner',
        earthMassForce,
      ),
    )
  }, [assembly.layers, assembly.usage_type, earthMassForce])

  return (
    <>
      <TableRow key={guid} sx={defaultSX}>
        <TableCell align="center">
          {/* Radio semantics: the current standard cannot be unchecked, only
              replaced by checking another assembly of the same usage type. */}
          <Tooltip
            title={assembly.isStandard ? 'Standardaufbau' : 'Als Standard setzen'}
            enterDelay={500}
          >
            <span>
              <Checkbox
                size="small"
                checked={assembly.isStandard}
                disabled={readOnly || assembly.isStandard || isSettingStandard}
                onClick={onSetStandard}
                data-cy={`${dataCy}-check`}
              />
            </span>
          </Tooltip>
        </TableCell>
        <TableCell>{assemblyKindToLabel[assembly.kind]}</TableCell>
        <TableCell>
          <AssemblyPlot
            assemblyType={assembly.usage_type}
            layers={assembly.layers}
            hasBorder={false}
            outsideWidth={100}
            earthMassForce={earthMassForce}
          />
        </TableCell>
        <TableCell>{name}</TableCell>
        <TableCell>{short_name}</TableCell>
        <TableCell align="center">
          <Typography data-cy={`${dataCy}-weight`}>{cumulativeWeight.toFixed(3)}</Typography>
        </TableCell>
        <TableCell align="right">
          <Box display="flex" justifyContent="flex-end">
            <IconButtonProgress
              isLoading={isDownloading}
              onClick={async () => {
                setIsDownloading(true)
                try {
                  await onDownload()
                } finally {
                  setIsDownloading(false)
                }
              }}
              tooltip="Aufbau Datenblatt herunterladen"
              tooltipProps={{ enterDelay: 500 }}
            >
              <DownloadIcon fontSize="small" />
            </IconButtonProgress>
            {!readOnly && (
              <AlertDialog
                title="Aufbauten verändern?"
                text={
                  isProjectAssembly
                    ? 'Änderungen an diesem Aufbau werden nur innerhalb des Projektes angewandt und wirken sich nicht auf andere Projekte aus.'
                    : 'Die Änderung am Aufbau wird nicht in bestehende Projekte übernommen. Die Aufbauten müssen <b>jeweils</b> in den entsprechenden Projekten im Aufbauten Manager geändert werden.'
                }
                onConfirm={onEdit}
              >
                <Tooltip title="Bearbeiten" enterDelay={500}>
                  <IconButton data-cy={`${dataCy}-btn-edit`}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </AlertDialog>
            )}
            {!readOnly && isFunction(onStore) ? (
              <Tooltip title="Im globalen Aufbauten Manager speichern">
                <IconButton onClick={onStore}>
                  <StorageIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <></>
            )}
            {!readOnly && (
              <Tooltip title="Duplizieren" enterDelay={500}>
                <IconButton onClick={onDuplicate}>
                  <Filter2Icon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="In Zwischenablage kopieren" enterDelay={500}>
              <IconButton onClick={onCopy} data-cy={`${dataCy}-copy`}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {!readOnly && (
              <AlertDialog
                title="Aufbauten löschen?"
                text="Sind Sie sicher, dass Sie diesen Aufbau löschen Wollen?"
                onConfirm={async () => onDelete()}
              >
                <Tooltip title="Löschen" enterDelay={500}>
                  <IconButton data-cy={`${dataCy}-delete`}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </AlertDialog>
            )}
          </Box>
        </TableCell>
      </TableRow>
    </>
  )
}

export default AssemblyListItem
